import { useMutation, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../../api";
import { createError, matchHttpError } from "../../../errors";
import { useErrorDispatcher } from "../../../hooks/useErrorDispatcher";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { learningCoachApplicationRequestsQueryKey } from "../SectionsLearningCoachApplicationRequests/useFetchLearningCoachApplicationRequests";
import {
  ISSUANCE_FAILED_MESSAGE,
  ISSUANCE_FORBIDDEN_MESSAGE,
  ISSUANCE_SUCCEEDED_MESSAGE,
  ISSUANCE_TIMEOUT_MESSAGE,
  STUDENT_NOT_ISSUABLE_MESSAGE,
  toRejectionMessage,
} from "./messages";
import { APPLICATION_REQUESTS_PATH } from "./types";

// 起票の失敗のうち、画面で文言を選ぶもの。401・メンテナンスなどは既存の共通処理に任せる
class IssuanceFailedError extends Error {}

// 申込 URL の発行（起票）。結果が返ったら、成否によらず申込一覧を取り直す。
// 失敗した場合は確認ダイアログを残したまま、理由をエラーフラッシュで示す（ISS-07）
export const useIssueApplicationRequest = ({
  sectionId,
  onSuccess,
}: {
  sectionId: string;
  onSuccess: () => void;
}) => {
  const queryClient = useQueryClient();
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const { sendUnauthorized, sendMaintenanceError, sendSteakEmergencyError } =
    useErrorDispatcher();

  return useMutation({
    mutationFn: async ({
      studentId,
      courseId,
    }: {
      studentId: string;
      courseId: number;
    }) => {
      const { response, data, error } = await boronClient.POST(
        APPLICATION_REQUESTS_PATH,
        {
          params: { path: { section_id: sectionId } },
          body: { student_id: studentId, course_id: courseId },
        },
      );
      if (response.ok && data) {
        return data;
      }
      // 起票の 422 は lc が断った理由（reasonCode）を持つ。共通の createError は 422 を errors の配列として読むため使わない
      if (response.status === 422 && error && "error" in error) {
        throw new IssuanceFailedError(
          toRejectionMessage(error.error.reasonCode),
        );
      }
      if (response.status === 403) {
        // 入力の後に、校舎が条件を満たさなくなった
        throw new IssuanceFailedError(ISSUANCE_FORBIDDEN_MESSAGE);
      }
      if (response.status === 504) {
        // lc で申込依頼ができていることがあるため、一覧を確かめてもらう
        throw new IssuanceFailedError(ISSUANCE_TIMEOUT_MESSAGE);
      }
      throw await createError(response);
    },
    // タイムアウトなど成否が分からない失敗でも lc で申込依頼ができていることがあるため、
    // 案内どおり一覧を確かめたときに古い表示のまま再発行させないよう、失敗時も取り直す
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: learningCoachApplicationRequestsQueryKey(sectionId),
      }),
    onSuccess: () => {
      showSuccessMessage(ISSUANCE_SUCCEEDED_MESSAGE);
      onSuccess();
    },
    onError: (error) => {
      if (error instanceof IssuanceFailedError) {
        showErrorMessage(error.message);
        return;
      }
      matchHttpError(error, {
        caseUnauthorized: sendUnauthorized,
        caseSteakEmergencyError: sendSteakEmergencyError,
        caseMaintenanceError: sendMaintenanceError,
        // 選んだ後に退塾・転校した生徒は、boron が校舎の生徒から引けず 404 を返す。
        // 時間をおいても発行できないため、仕様書 No.2 の「生徒が無効」と同じ文言で示す
        caseNotFound: () => showErrorMessage(STUDENT_NOT_ISSUABLE_MESSAGE),
        caseTooManyRequests: () => showErrorMessage(ISSUANCE_FAILED_MESSAGE),
        caseOthers: () => showErrorMessage(ISSUANCE_FAILED_MESSAGE),
      });
    },
  });
};
